import { useActionState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { siteConfig } from '../../config/siteConfig';
import { Link, useNavigate } from 'react-router-dom';
import { userService } from '../../apiServices/UserService';
import Alert, { AlertType } from '../../components/Alert/Alert';
import Button, { ButtonType } from '../../components/Button/Button';
import { useToast } from '../../context/Toast/useToast';
import { useUser } from '../../context/User/useUser';
import '../Login/Login.css';

type SignUpState = {
  alert?: AlertType;
  success?: boolean;
};

function SignUp() {
  const { setIsAuthenticated, fetchUser } = useUser();
  const navigate = useNavigate();
  const toast = useToast();
  const intl = useIntl();

  async function signUpAction(
    _prevState: SignUpState,
    formData: FormData
  ): Promise<SignUpState> {
    const email = formData.get('email') as string;
    const name = formData.get('name') as string;
    const isAcceptingMarketing = formData.get('marketing') === 'on';

    try {
      await userService.signUp(email, name, isAcceptingMarketing);

      toast.addToast({
        message: intl.formatMessage(
          { id: 'signUp.successToast' },
          { domain: siteConfig.domain }
        ),
        type: 'success',
      });

      return { success: true };
    } catch (error) {
      return {
        alert: {
          message:
            error instanceof Error
              ? error.message
              : intl.formatMessage({ id: 'signUp.error' }),
          type: 'error',
        },
      };
    }
  }

  const [state, formAction, isPending] = useActionState(signUpAction, {});

  useEffect(() => {
    if (state.success) {
      setIsAuthenticated();
      fetchUser();
      navigate('/');
    }
  }, [state.success, setIsAuthenticated, fetchUser, navigate]);

  return (
    <div className={'login'}>
      <a href={`https://${siteConfig.domain}`} target="_blank">
        <img className="logo" src={siteConfig.logoSrc} alt="" />
      </a>
      <div className="onmobile-login-info">
        <div>
          <span>
            <FormattedMessage id="signUp.mobileWarningTitle" />
          </span>
          <h3>
            <FormattedMessage id="signUp.mobileWarningHeading" />
          </h3>
          <p>
            <FormattedMessage
              id="signUp.mobileWarningText"
              values={{ appName: siteConfig.appName }}
            />
          </p>
        </div>
      </div>
      <div className="input-form">
        <h1 className="info">
          <FormattedMessage id="signUp.heading" />
        </h1>
        <form action={formAction}>
          <div className="email-container">
            <label className="login-label" htmlFor="name">
              <FormattedMessage id="signUp.nameLabel" />
            </label>
            <input
              id="name"
              name="name"
              className="email-input"
              type="text"
              placeholder={intl.formatMessage({ id: 'signUp.namePlaceholder' })}
              required
              disabled={isPending}
            />
          </div>
          <div className="email-container">
            <label className="login-label" htmlFor="email">
              <FormattedMessage id="signUp.emailLabel" />
            </label>
            <input
              id="email"
              name="email"
              className="email-input"
              type="email"
              placeholder={intl.formatMessage({
                id: 'signUp.emailPlaceholder',
              })}
              required
              disabled={isPending}
            />
          </div>
          <div className="login-checkbox">
            <input
              id="terms"
              name="terms"
              className="terms-checkbox"
              type="checkbox"
              required
              disabled={isPending}
            />{' '}
            <label htmlFor="terms">
              <FormattedMessage
                id="signUp.termsCheckbox"
                values={{ appName: siteConfig.appName }}
              />{' '}
              <Link to={siteConfig.termsUrl} target="_blank">
                <FormattedMessage id="signUp.termsLink" />
              </Link>
            </label>
          </div>
          <div className="login-checkbox">
            <input
              id="marketing"
              name="marketing"
              className="marketing-checkbox"
              type="checkbox"
              disabled={isPending}
            />{' '}
            <label htmlFor="marketing">
              <FormattedMessage
                id="signUp.marketingCheckbox"
                values={{ domain: siteConfig.domain }}
              />
            </label>
          </div>
          <Button
            htmlType="submit"
            type={ButtonType.Primary}
            className="login-submit"
            loading={isPending}
            loadingText={intl.formatMessage({ id: 'signUp.submitRegistering' })}
          >
            <FormattedMessage id="signUp.submitButton" />
          </Button>
        </form>

        <Link to={'/sign-in'}>
          <FormattedMessage id="signUp.signInLink" />
        </Link>

        <p className="support">
          <FormattedMessage id="signUp.support" />{' '}
          <span className="bold">{siteConfig.contactEmail}</span>
        </p>

        <Alert alert={state.alert} />
      </div>
    </div>
  );
}

export default SignUp;
